import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ProfileCoverageCard } from 'features/top-three-profiles/ProfileCoverageCard';
import { normalizeCoverage } from 'features/top-three-profiles/topThreeProfiles';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { Button } from 'shared/components/Button';
import { Badge, Card, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { useToast } from 'shared/components/Toast';
import { SwitchField, TextField } from 'shared/forms/Fields';
import { canPublishVersion, formatDateTime, versionBlockingReasons } from 'shared/utilities/format';

function versionStatusLabel(status, t) {
  if (!status) {
    return '—';
  }
  const key = `versions.statuses.${status}`;
  const label = t(key);
  return label === key ? status : label;
}

function versionStatusTone(status) {
  if (status === 'published') {
    return 'success';
  }
  if (status === 'built' || status === 'draft') {
    return 'info';
  }
  return 'neutral';
}

function coverageForDisplay(coverage) {
  if (!coverage) {
    return null;
  }
  return normalizeCoverage(coverage);
}

async function invalidateVersionQueries(queryClient, id) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.versions }),
    queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
    id ? queryClient.invalidateQueries({ queryKey: queryKeys.version(id) }) : Promise.resolve(),
    id ? queryClient.invalidateQueries({ queryKey: queryKeys.topThreeProfileCoverage(id) }) : Promise.resolve(),
  ]);
}

export function VersionsPage() {
  const { t } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.versionsManage);
  const [acknowledgeZero, setAcknowledgeZero] = useState(false);
  const [label, setLabel] = useState('');
  const [preflightMeta, setPreflightMeta] = useState(null);

  const versionsQuery = useQuery({ queryKey: queryKeys.versions, queryFn: ({ signal }) => assessmentApi.listVersions({ signal }) });
  const overviewQuery = useQuery({ queryKey: queryKeys.overview, queryFn: ({ signal }) => assessmentApi.overview({ signal }) });
  const items = versionsQuery.data?.items || [];
  const latest = items[0];
  const coverageQuery = useQuery({
    queryKey: queryKeys.topThreeProfileCoverage(latest?.id),
    enabled: Boolean(latest?.id),
    queryFn: ({ signal }) => assessmentApi.getTopThreeProfileCoverage(latest.id, { signal }),
  });
  const preflight = useMutation({
    mutationFn: () => assessmentApi.preflight({ acknowledge_zero_weights: acknowledgeZero }),
    onSuccess: (data) => {
      setPreflightMeta({
        data,
        revision: data.revision ?? overviewQuery.data?.revision,
        checksum: data.configuration_checksum,
        acknowledged: acknowledgeZero,
      });
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });
  const build = useMutation({
    mutationFn: () => assessmentApi.buildVersion({ acknowledge_zero_weights: acknowledgeZero, label }),
    onSuccess: async () => {
      setPreflightMeta(null);
      await invalidateVersionQueries(queryClient);
      toast.success(t('status.created'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });
  const publish = useMutation({
    mutationFn: (id) => assessmentApi.publishVersion(id),
    onSuccess: async (_data, id) => {
      await invalidateVersionQueries(queryClient, id);
      toast.success(t('status.published'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  if (versionsQuery.isLoading || overviewQuery.isLoading) return <Skeleton rows={6} />;
  if (versionsQuery.isError) return <ErrorState title={humanizeApiError(versionsQuery.error, t)} onRetry={versionsQuery.refetch} retryLabel={t('actions.retry')} />;

  const published = items.find((item) => item.status === 'published' && item.is_current);
  const workingRevision = overviewQuery.data?.revision;
  const preflightCurrent = Boolean(
    preflightMeta
    && preflightMeta.revision === workingRevision
    && preflightMeta.checksum === (preflightMeta.data?.configuration_checksum)
    && preflightMeta.acknowledged === acknowledgeZero,
  );
  const preflightData = preflightCurrent ? preflightMeta.data : null;
  const hasZeroWarning = (preflightData?.warnings || []).some((warning) => String(warning.code || '').includes('zero'));
  const ackOk = !hasZeroWarning || acknowledgeZero;
  const canBuild = preflightData?.can_build === true && ackOk && !build.isPending;
  const checksum = latest?.configuration_checksum || '—';
  const latestCoverage = coverageQuery.data || latest?.profile_coverage || latest?.coverage;

  return (
    <div className="page-stack">
      <PageHeader title={t('versions.title')} description={t('versions.workflow')} />
      <div className="stat-grid">
        <Card className="stat-card">
          <p className="muted">{t('versions.workingRevision')}</p>
          <strong className="stat">{workingRevision ?? '—'}</strong>
        </Card>
        <Card className="stat-card">
          <p className="muted">{t('versions.latestBuilt')}</p>
          <strong className="stat">{latest ? `#${latest.number}` : '—'}</strong>
        </Card>
        <Card className="stat-card">
          <p className="muted">{t('versions.latestPublished')}</p>
          <strong className="stat">{published ? `#${published.number}` : '—'}</strong>
        </Card>
        <Card className="stat-card">
          <p className="muted">{t('versions.checksum')}</p>
          <strong className="stat-fingerprint ltr-id" title={checksum === '—' ? undefined : checksum}>{checksum}</strong>
        </Card>
      </div>
      {latestCoverage ? <ProfileCoverageCard coverage={latestCoverage} t={t} /> : null}
      {canManage ? (
        <div className="card version-build-form">
          <SwitchField id="ack-zero" label={t('versions.acknowledgeZero')} checked={acknowledgeZero} onChange={(value) => { setAcknowledgeZero(value); setPreflightMeta(null); }} />
          <div className="version-build-bar">
            <TextField
              id="version-label"
              label={t('versions.label')}
              value={label}
              onChange={setLabel}
              placeholder={t('versions.labelPlaceholder')}
              autoComplete="off"
            />
            <div className="toolbar wrap">
              <Button onClick={() => preflight.mutate()} loading={preflight.isPending} disabled={preflight.isPending}>{t('actions.preflight')}</Button>
              <Button
                tone="secondary"
                disabled={!canBuild}
                loading={build.isPending}
                onClick={async () => {
                  if (await confirm({ body: t('confirm.build'), tone: 'primary' })) build.mutate();
                }}
              >
                {t('actions.build')}
              </Button>
            </div>
          </div>
          {preflightMeta && !preflightCurrent ? <p className="warning-banner">{t('versions.preflightStale')}</p> : null}
          {preflightData ? (
            <div className="version-preflight">
              <p className={preflightData.can_build ? 'success-banner' : 'warning-banner'} role="status">
                {preflightData.can_build ? t('versions.canBuild') : t('versions.cannotBuild')}
              </p>
              <dl className="definition-grid">
                <div className="definition-row">
                  <dt>{t('builder.questionCount')}</dt>
                  <dd>{preflightData.active_questions ?? '—'}</dd>
                </div>
              </dl>
              {(preflightData.warnings || []).length > 0 ? (
                <ul>
                  {(preflightData.warnings || []).map((warning) => (
                    <li key={warning.code} className={warning.blocks_build ? 'field-error' : 'muted'}>
                      {warning.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {items.length === 0 ? <EmptyState title={t('empty.versions')} /> : (
        <ListTable caption={t('versions.title')} stickyActions>
          <thead>
            <tr>
              <th>{t('versions.number')}</th>
              <th>{t('versions.status')}</th>
              <th>{t('profiles.completeness')}</th>
              <th>{t('versions.publishedAt')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((version) => {
              const rawCoverage = version.id === latest?.id
                ? (latestCoverage || version.profile_coverage || version.coverage)
                : (version.profile_coverage || version.coverage);
              const coverage = coverageForDisplay(rawCoverage);
              const publishable = canPublishVersion(version);
              const publishDisabled = publish.isPending || !publishable;
              const blockers = versionBlockingReasons(version);
              const configured = coverage
                ? t('profiles.configuredFraction', {
                  complete: coverage.complete,
                  expected: coverage.expectedProfiles,
                })
                : (version.top_three_profiles_count != null
                  ? t('profiles.configuredFraction', {
                    complete: '—',
                    expected: version.top_three_profiles_count,
                  })
                  : '—');
              return (
                <tr key={version.id}>
                  <td>#{version.number}</td>
                  <td><Badge tone={versionStatusTone(version.status)}>{versionStatusLabel(version.status, t)}</Badge></td>
                  <td>
                    <span data-testid={`version-coverage-${version.id}`}>{configured}</span>
                    {coverage && !coverage.isComplete ? (
                      <span className="sr-only">{t('profiles.coverageIncompleteWarning')}</span>
                    ) : null}
                    {coverage && !coverage.isComplete ? (
                      <Badge tone="warning">{t('profiles.incomplete')}</Badge>
                    ) : null}
                  </td>
                  <td>{formatDateTime(version.published_at)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${version.number}`}
                    items={[
                      detailsAction(paths.versionDetail(version.id), t),
                      canManage && publishable ? {
                        key: 'publish',
                        label: t('actions.publish'),
                        disabled: publishDisabled,
                        onClick: async () => {
                          if (await confirm({ body: t('confirm.publish') })) publish.mutate(version.id);
                        },
                      } : null,
                      ...(canManage && !publishable && version.status !== 'published'
                        ? (blockers.length > 0
                          ? blockers.map((reason) => ({
                            key: reason.code || reason.message,
                            label: reason.message,
                            disabled: true,
                          }))
                          : [{
                            key: 'not-publishable',
                            label: t('versions.notPublishable'),
                            disabled: true,
                          }])
                        : []),
                    ]}
                  />
                </tr>
              );
            })}
          </tbody>
        </ListTable>
      )}
    </div>
  );
}

export function VersionDetailPage() {
  const { t } = useI18n();
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.versionsManage);
  const query = useQuery({ queryKey: queryKeys.version(id), queryFn: ({ signal }) => assessmentApi.showVersion(id, { signal }) });
  const coverageQuery = useQuery({
    queryKey: queryKeys.topThreeProfileCoverage(id),
    enabled: Boolean(id),
    queryFn: ({ signal }) => assessmentApi.getTopThreeProfileCoverage(id, { signal }),
  });
  const publish = useMutation({
    mutationFn: () => assessmentApi.publishVersion(id),
    onSuccess: async () => {
      await invalidateVersionQueries(queryClient, id);
      toast.success(t('status.published'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  const version = query.data;
  const coverage = coverageQuery.data || version.profile_coverage || version.coverage;
  const publishable = canPublishVersion(version);
  const publishDisabled = publish.isPending || !publishable;
  const blockers = versionBlockingReasons(version);
  const checksum = version.configuration_checksum || '—';
  return (
    <div className="page-stack">
      <PageHeader title={`${t('nav.versions')} #${version.number}`} backTo={paths.versions} />
      <Card>
        <dl className="definition-grid">
          {version.label ? (
            <div className="definition-row">
              <dt>{t('versions.label')}</dt>
              <dd>{version.label}</dd>
            </div>
          ) : null}
          <div className="definition-row">
            <dt>{t('versions.status')}</dt>
            <dd><Badge tone={versionStatusTone(version.status)}>{versionStatusLabel(version.status, t)}</Badge></dd>
          </div>
          <div className="definition-row">
            <dt>{t('versions.checksum')}</dt>
            <dd className="stat-fingerprint ltr-id" title={checksum === '—' ? undefined : checksum}>{checksum}</dd>
          </div>
        </dl>
        {canManage && publishable ? (
          <div className="toolbar wrap">
            <Button
              disabled={publishDisabled}
              loading={publish.isPending}
              onClick={async () => {
                if (await confirm({ body: t('confirm.publish') })) publish.mutate();
              }}
            >
              {t('actions.publish')}
            </Button>
          </div>
        ) : null}
        {!publishable && version.status !== 'published' ? (
          <ul data-testid="version-blocking-reasons">
            {(blockers.length > 0 ? blockers : [{ message: t('versions.notPublishable') }]).map((reason) => (
              <li key={reason.code || reason.message} className="warning-banner">{reason.message}</li>
            ))}
          </ul>
        ) : null}
        {canManage && version.status !== 'archived' && version.acknowledged_zero_weights !== true ? (
          <div className="toolbar wrap">
            <p className="muted">{t('versions.storedZeroMissing')}</p>
            <Button
              tone="secondary"
              data-testid="acknowledge-stored-zero-weights"
              onClick={async () => {
                if (await confirm({ body: t('versions.acknowledgeStoredZero') })) {
                  try {
                    await assessmentApi.acknowledgeVersionZeroWeights(id);
                    await invalidateVersionQueries(queryClient, id);
                    toast.success(t('versions.storedZeroRecorded'));
                  } catch (error) {
                    toast.error(humanizeApiError(error, t));
                  }
                }
              }}
            >
              {t('versions.acknowledgeStoredZero')}
            </Button>
          </div>
        ) : null}
        {version.acknowledged_zero_weights === true ? (
          <p className="muted" data-testid="stored-zero-weight-acknowledgement">{t('versions.storedZeroRecorded')}</p>
        ) : null}
      </Card>
      {coverage ? <ProfileCoverageCard coverage={coverage} t={t} /> : null}
    </div>
  );
}
